import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Zap, Brain, Dumbbell, Heart } from 'lucide-react';
const IngredientsOverview = () => {
  const activeIngredients = [{
    name: "L-Citrulline malate (2:1)",
    singleDose: "7,500mg",
    doubleDose: "15,000mg",
    benefit: "Maksimal pump og blodstrøm",
    icon: <Heart className="w-5 h-5" />,
    color: "text-red-400",
    subItems: [
      {
        name: "L-Citrulline",
        singleDose: "5,000mg",
        doubleDose: "10,000mg",
        benefit: "Ren citrullin for pump"
      },
      {
        name: "Malic Acid", 
        singleDose: "2,500mg",
        doubleDose: "5,000mg",
        benefit: "Energiproduksjon"
      }
    ]
  }, {
    name: "Betaine Anhydrous",
    singleDose: "2,500mg",
    doubleDose: "5,000mg",
    benefit: "Økt kraft og cellhydrering",
    icon: <Dumbbell className="w-5 h-5" />,
    color: "text-blue-400"
  }, {
    name: "Taurine",
    singleDose: "1,500mg",
    doubleDose: "3,000mg",
    benefit: "Muskelfunksjon og utholdenhet",
    icon: <Zap className="w-5 h-5" />,
    color: "text-yellow-400"
  }, {
    name: "L-Tyrosine",
    singleDose: "1,500mg",
    doubleDose: "3,000mg",
    benefit: "Mental fokus og klarhet",
    icon: <Brain className="w-5 h-5" />,
    color: "text-purple-400"
  }, {
    name: "Bitter Orange Extract",
    singleDose: "526mg",
    doubleDose: "1,053mg",
    benefit: "Naturlig energi og fokus",
    icon: <Zap className="w-5 h-5" />,
    color: "text-orange-400"
  }, {
    name: "Pink Himalayan Sea Salt",
    singleDose: "250mg",
    doubleDose: "500mg",
    benefit: "Elektrolytbalanse",
    icon: <Heart className="w-5 h-5" />,
    color: "text-pink-400"
  }, {
    name: "Niacin",
    singleDose: "8mg",
    doubleDose: "16mg",
    benefit: "Energimetabolisme (100% DV)",
    icon: <Zap className="w-5 h-5" />,
    color: "text-primary"
  }, {
    name: "Sodium",
    singleDose: "100mg",
    doubleDose: "200mg",
    benefit: "Elektrolytbalanse",
    icon: <Heart className="w-5 h-5" />,
    color: "text-blue-300"
  }, {
    name: "Potassium",
    singleDose: "95mg",
    doubleDose: "190mg",
    benefit: "Elektrolytbalanse (9.5% DV)",
    icon: <Heart className="w-5 h-5" />,
    color: "text-green-300"
  }];
  const additionalIngredients = ["Klumpförebyggande medel (Kiseldioxid)", "Sötningsmedel (Aspartam, Acesulfam K)", "Aromer", "Färgämnen (Riboflavin, Klorofyllinkopparkomplex)"];
  return <div className="mb-8">
      {/* Supplement Facts Label - Website styled */}
      <div className="bg-muted border border-border p-2 md:p-4 backdrop-blur-sm">
        {/* Header */}
        <div className="border-b border-border pb-1 md:pb-2 mb-2 md:mb-3">
          <h2 className="text-lg md:text-xl font-bold text-primary font-orbit mb-0.5 md:mb-1">Innholdsliste</h2>
          <p className="text-xs md:text-sm text-muted-foreground">Servingstørrelse: 1-2 skjeer (15-31g)</p>
          <p className="text-xs md:text-sm text-muted-foreground">Serveringer per boks: 24 (1 skje), 12 (2 skjeer)</p>
        </div>

        {/* Column headers */}
        <div className="grid grid-cols-3 gap-2 md:gap-4 text-xs md:text-sm font-semibold text-primary border-b border-border pb-1 mb-1 md:mb-2">
          <span>Ingrediens</span>
          <span className="text-center">Per 1 skje</span>
          <span className="text-center">Per 2 skjeer</span>
        </div>

        {/* Active ingredients */}
        <div className="space-y-0.5 md:space-y-1">
          {activeIngredients.map((ingredient, index) => (
            <div key={index}>
              <div className="grid grid-cols-3 gap-2 md:gap-4 text-xs md:text-sm py-0.5 md:py-1 border-b border-border/20 hover:bg-primary/10 transition-colors">
                <span className="font-medium text-foreground">{ingredient.name}</span>
                <span className="text-center text-primary">{ingredient.singleDose}</span>
                <span className="text-center font-semibold text-primary">{ingredient.doubleDose}</span>
              </div>
              {ingredient.subItems && ingredient.subItems.map((subItem, subIndex) => (
                <div key={`${index}-${subIndex}`} className="grid grid-cols-3 gap-2 md:gap-4 text-xs md:text-sm py-0.5 md:py-1 pl-4 md:pl-6 border-b border-border/10">
                  <span className="text-muted-foreground">• {subItem.name}</span>
                  <span className="text-center text-muted-foreground">{subItem.singleDose}</span>
                  <span className="text-center text-muted-foreground">{subItem.doubleDose}</span>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Additional ingredients */}
        <div className="mt-2 md:mt-3 pt-1.5 md:pt-2 border-t border-border">
          <p className="text-xs md:text-sm font-semibold text-primary mb-0.5 md:mb-1">Andre ingredienser:</p>
          <p className="text-xs text-muted-foreground leading-tight">
            {additionalIngredients.join(", ")}
          </p>
        </div>

        {/* Footer note */}
        <div className="mt-2 md:mt-3 pt-1.5 md:pt-2 border-t border-border bg-brand-electricBlue/10 -m-2 md:-m-4 mt-2 md:mt-3 p-2 md:p-4">
          <p className="text-center text-primary text-xs md:text-sm font-medium">
            ✅ Alle ingredienser er dosert basert på kliniske studier
          </p>
        </div>
      </div>
    </div>;
};
export default IngredientsOverview;